import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../config';
import { DollarSign, TrendingUp, Edit2, Trash2, X, User, CreditCard, MessageSquare, Gift, Save } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

const API = `${BACKEND_URL}/api`;

const CalendarioVentas = ({ isOpen, onClose, empleadoId, empleadoNombre, mostrarTodos = false }) => {
  const [mesActual, setMesActual] = useState(new Date().getMonth() + 1);
  const [anoActual, setAnoActual] = useState(new Date().getFullYear());
  const [ventas, setVentas] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Estados para el modal de detalle del día
  const [diaSeleccionado, setDiaSeleccionado] = useState(null);
  const [reportesDelDia, setReportesDelDia] = useState([]);
  const [modalDetalleAbierto, setModalDetalleAbierto] = useState(false);
  
  // Estados para edición
  const [reporteEditando, setReporteEditando] = useState(null);
  const [formEdicion, setFormEdicion] = useState({
    subs_amount: 0,
    messages_amount: 0,
    tips_amount: 0,
    notes: ''
  });

  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const fetchVentas = useCallback(async () => {
    setLoading(true);
    try {
      const fecha_inicio = `${anoActual}-${String(mesActual).padStart(2, '0')}-01`;
      const ultimoDia = new Date(anoActual, mesActual, 0).getDate();
      const fecha_fin = `${anoActual}-${String(mesActual).padStart(2, '0')}-${ultimoDia}`;

      // Obtener reportes de chatters
      const response = await axios.get(`${API}/reports`, { withCredentials: true });
      let todosReportes = response.data.map(r => ({ ...r, tipo_reporte: 'chatter' }));

      // Si mostrarTodos, también obtener reportes del CEO
      if (mostrarTodos) {
        try {
          const ceoResponse = await axios.get(`${API}/reports/ceo`, { withCredentials: true });
          const ceoReportes = ceoResponse.data.map(r => ({
            ...r,
            user_id: r.user_id || 'ceo',
            user_name: r.user_name || 'CEO',
            money_collected: (r.subs_amount || 0) + (r.messages_amount || 0) + (r.tips_amount || 0),
            group_name: r.group_name || 'CEO',
            tipo_reporte: 'ceo'
          }));
          todosReportes = [...todosReportes, ...ceoReportes];
        } catch (e) {
          console.log('No CEO reports access');
        }
      }

      // Filtrar por mes Y opcionalmente por empleado
      const reportesFiltrados = todosReportes.filter(r => {
        const enRango = r.date >= fecha_inicio && r.date <= fecha_fin;
        if (mostrarTodos) {
          return enRango;
        }
        return enRango && r.user_id === empleadoId;
      });

      // Agrupar por fecha
      const ventasPorDia = {};
      let totalVentas = 0;

      reportesFiltrados.forEach(reporte => {
        const fecha = reporte.date;
        const ventasReporte = reporte.money_collected || 0;

        if (!ventasPorDia[fecha]) {
          ventasPorDia[fecha] = { fecha, total: 0, reportes: [] };
        }

        ventasPorDia[fecha].total += ventasReporte;
        ventasPorDia[fecha].reportes.push(reporte);
        totalVentas += ventasReporte;
      });

      const ventasArray = Object.values(ventasPorDia);
      setVentas(ventasArray);

      setResumen({
        total_ventas: totalVentas,
        dias_con_ventas: ventasArray.length,
        promedio_dia: ventasArray.length > 0 ? totalVentas / ventasArray.length : 0,
        dia_mayor_venta: ventasArray.length > 0 ? Math.max(...ventasArray.map(v => v.total)) : 0
      });

    } catch (error) {
      console.error('Error fetching ventas:', error);
    } finally {
      setLoading(false);
    }
  }, [empleadoId, mesActual, anoActual, mostrarTodos]);

  useEffect(() => {
    if (isOpen) {
      fetchVentas();
    }
  }, [isOpen, fetchVentas]);

  const getDiasDelMes = () => {
    const primerDia = new Date(anoActual, mesActual - 1, 1);
    const ultimoDia = new Date(anoActual, mesActual, 0).getDate();
    const primerDiaSemana = primerDia.getDay();
    
    const dias = [];
    for (let i = 0; i < primerDiaSemana; i++) {
      dias.push(null);
    }
    for (let i = 1; i <= ultimoDia; i++) {
      dias.push(i);
    }
    return dias;
  };

  const getVentasDelDia = (dia) => {
    if (!dia) return null;
    const fechaStr = `${anoActual}-${String(mesActual).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
    return ventas.find(v => v.fecha === fechaStr);
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}K`;
    }
    return `$${Math.round(amount)}`;
  };

  const formatFullCurrency = (amount) => {
    return `$${(amount || 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const cambiarMes = (direccion) => {
    if (direccion === 'anterior') {
      if (mesActual === 1) {
        setMesActual(12);
        setAnoActual(anoActual - 1);
      } else {
        setMesActual(mesActual - 1);
      }
    } else {
      if (mesActual === 12) {
        setMesActual(1);
        setAnoActual(anoActual + 1);
      } else {
        setMesActual(mesActual + 1);
      }
    }
  };

  // Abrir modal de detalle del día
  const abrirDetalleDia = (dia) => {
    const ventasDia = getVentasDelDia(dia);
    if (ventasDia && ventasDia.reportes.length > 0) {
      const fechaStr = `${anoActual}-${String(mesActual).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
      setDiaSeleccionado(fechaStr);
      setReportesDelDia(ventasDia.reportes);
      setModalDetalleAbierto(true);
      setReporteEditando(null);
    }
  };

  // Iniciar edición de un reporte
  const iniciarEdicion = (reporte) => {
    setReporteEditando(reporte);
    setFormEdicion({
      subs_amount: reporte.subs_amount || 0,
      messages_amount: reporte.messages_amount || 0,
      tips_amount: reporte.tips_amount || 0,
      notes: reporte.notes || ''
    });
  };

  // Cancelar edición
  const cancelarEdicion = () => {
    setReporteEditando(null);
    setFormEdicion({
      subs_amount: 0,
      messages_amount: 0,
      tips_amount: 0,
      notes: ''
    });
  };

  // Guardar cambios del reporte
  const guardarCambios = async () => {
    if (!reporteEditando) return;

    try {
      const totalMoney = parseFloat(formEdicion.subs_amount) + parseFloat(formEdicion.messages_amount) + parseFloat(formEdicion.tips_amount);

      if (reporteEditando.tipo_reporte === 'ceo') {
        // Actualizar reporte CEO
        await axios.put(`${API}/reports/ceo/${reporteEditando.id}`, {
          subs_amount: parseFloat(formEdicion.subs_amount),
          messages_amount: parseFloat(formEdicion.messages_amount),
          tips_amount: parseFloat(formEdicion.tips_amount),
          notes: formEdicion.notes
        }, { withCredentials: true });
      } else {
        // Actualizar reporte de chatter usando endpoint PATCH simplificado
        await axios.patch(`${API}/reports/${reporteEditando.id}/financials`, {
          subs_amount: parseFloat(formEdicion.subs_amount),
          messages_amount: parseFloat(formEdicion.messages_amount),
          tips_amount: parseFloat(formEdicion.tips_amount),
          notes: formEdicion.notes
        }, { withCredentials: true });
      }

      toast.success('Reporte actualizado correctamente');
      cancelarEdicion();
      setModalDetalleAbierto(false);
      fetchVentas(); // Refrescar datos
    } catch (error) {
      console.error('Error al actualizar reporte:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar el reporte');
    }
  };

  // Eliminar reporte
  const eliminarReporte = async (reporte) => {
    if (!window.confirm('¿Estás seguro de eliminar este reporte? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      if (reporte.tipo_reporte === 'ceo') {
        await axios.delete(`${API}/reports/ceo/${reporte.id}`, { withCredentials: true });
      } else {
        await axios.delete(`${API}/reports/${reporte.id}`, { withCredentials: true });
      }

      toast.success('Reporte eliminado correctamente');
      
      // Actualizar lista de reportes del día
      const nuevosReportes = reportesDelDia.filter(r => r.id !== reporte.id);
      setReportesDelDia(nuevosReportes);
      
      if (nuevosReportes.length === 0) {
        setModalDetalleAbierto(false);
      }
      
      fetchVentas(); // Refrescar datos
    } catch (error) {
      console.error('Error al eliminar reporte:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar el reporte');
    }
  };

  // Formatear fecha para mostrar
  const formatearFecha = (fechaStr) => {
    const [year, month, day] = fechaStr.split('-');
    return `${day} de ${meses[parseInt(month) - 1]} ${year}`;
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              📅 Calendario de Ventas {mostrarTodos ? '(Todas)' : `- ${empleadoNombre}`}
            </DialogTitle>
          </DialogHeader>

          {/* Resumen */}
          {resumen && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Card className="bg-zinc-800 border-zinc-700">
                <CardContent className="p-3 text-center">
                  <DollarSign className="w-5 h-5 mx-auto text-green-400 mb-1" />
                  <p className="text-xs text-gray-400">Total Mes</p>
                  <p className="text-lg font-bold text-green-400">${resumen.total_ventas.toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card className="bg-zinc-800 border-zinc-700">
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-gray-400">Días con Ventas</p>
                  <p className="text-lg font-bold text-white">{resumen.dias_con_ventas}</p>
                </CardContent>
              </Card>
              <Card className="bg-zinc-800 border-zinc-700">
                <CardContent className="p-3 text-center">
                  <TrendingUp className="w-5 h-5 mx-auto text-blue-400 mb-1" />
                  <p className="text-xs text-gray-400">Promedio/Día</p>
                  <p className="text-lg font-bold text-blue-400">${Math.round(resumen.promedio_dia).toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card className="bg-zinc-800 border-zinc-700">
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-gray-400">Mejor Día</p>
                  <p className="text-lg font-bold text-yellow-400">${resumen.dia_mayor_venta.toLocaleString()}</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Navegación */}
          <div className="flex items-center justify-between mb-4">
            <Button variant="outline" size="sm" onClick={() => cambiarMes('anterior')} className="border-zinc-700 text-white hover:bg-zinc-800">
              ← Anterior
            </Button>
            <h3 className="text-lg font-semibold text-white">
              {meses[mesActual - 1]} {anoActual}
            </h3>
            <Button variant="outline" size="sm" onClick={() => cambiarMes('siguiente')} className="border-zinc-700 text-white hover:bg-zinc-800">
              Siguiente →
            </Button>
          </div>

          {/* Instrucción */}
          <p className="text-xs text-gray-500 text-center mb-2">
            💡 Haz clic en un día con ventas para ver el detalle y editar
          </p>

          {/* Calendario */}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(dia => (
                <div key={dia} className="text-center text-xs font-medium text-gray-400 py-2">{dia}</div>
              ))}
              {getDiasDelMes().map((dia, index) => {
                const ventasDia = getVentasDelDia(dia);
                const tieneVentas = ventasDia && ventasDia.total > 0;
                return (
                  <div
                    key={index}
                    onClick={() => tieneVentas && abrirDetalleDia(dia)}
                    className={`aspect-square p-1 rounded-lg text-center transition-all ${
                      dia ? 'bg-zinc-800' : ''
                    } ${tieneVentas ? 'ring-2 ring-green-500 cursor-pointer hover:bg-zinc-700 hover:scale-105' : ''}`}
                    data-testid={dia ? `calendar-day-${dia}` : undefined}
                  >
                    {dia && (
                      <>
                        <div className="text-xs text-gray-400">{dia}</div>
                        {tieneVentas && (
                          <div className="text-xs font-bold text-green-400 mt-1">
                            {formatCurrency(ventasDia.total)}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Detalle del Día */}
      <Dialog open={modalDetalleAbierto} onOpenChange={setModalDetalleAbierto}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-white flex items-center gap-2">
              📋 Detalle de Facturación - {diaSeleccionado && formatearFecha(diaSeleccionado)}
            </DialogTitle>
          </DialogHeader>

          {/* Lista de reportes del día */}
          <div className="space-y-4">
            {reportesDelDia.map((reporte, idx) => (
              <Card key={reporte.id || idx} className="bg-zinc-800 border-zinc-700">
                <CardContent className="p-4">
                  {reporteEditando?.id === reporte.id ? (
                    /* Modo edición */
                    <div className="space-y-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs px-2 py-1 rounded ${
                          reporte.tipo_reporte === 'ceo' 
                            ? 'bg-purple-500/20 text-purple-400' 
                            : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {reporte.tipo_reporte === 'ceo' ? '👑 Reporte CEO' : '💬 Reporte Chatter'}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={cancelarEdicion}
                          className="text-gray-400 hover:text-white"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 gap-3">
                        <div>
                          <label className="text-xs text-gray-400 flex items-center gap-1 mb-1">
                            <CreditCard className="w-3 h-3" /> Suscripciones (Subs)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={formEdicion.subs_amount}
                            onChange={(e) => setFormEdicion({ ...formEdicion, subs_amount: e.target.value })}
                            className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2 text-white text-sm"
                            data-testid="edit-subs-amount"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-400 flex items-center gap-1 mb-1">
                            <MessageSquare className="w-3 h-3" /> Mensajes
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={formEdicion.messages_amount}
                            onChange={(e) => setFormEdicion({ ...formEdicion, messages_amount: e.target.value })}
                            className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2 text-white text-sm"
                            data-testid="edit-messages-amount"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-400 flex items-center gap-1 mb-1">
                            <Gift className="w-3 h-3" /> Tips
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={formEdicion.tips_amount}
                            onChange={(e) => setFormEdicion({ ...formEdicion, tips_amount: e.target.value })}
                            className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2 text-white text-sm"
                            data-testid="edit-tips-amount"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-400 mb-1 block">Notas</label>
                          <textarea
                            value={formEdicion.notes}
                            onChange={(e) => setFormEdicion({ ...formEdicion, notes: e.target.value })}
                            className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-2 text-white text-sm resize-none"
                            rows={2}
                            data-testid="edit-notes"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={cancelarEdicion}
                          className="border-zinc-600 text-gray-400 hover:text-white"
                        >
                          Cancelar
                        </Button>
                        <Button
                          size="sm"
                          onClick={guardarCambios}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid="save-edit-btn"
                        >
                          <Save className="w-4 h-4 mr-1" /> Guardar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* Modo visualización */
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            reporte.tipo_reporte === 'ceo' 
                              ? 'bg-purple-500/20 text-purple-400' 
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {reporte.tipo_reporte === 'ceo' ? '👑 CEO' : '💬 Chatter'}
                          </span>
                          <span className="text-sm text-white font-medium flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {reporte.user_name || 'Sin nombre'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => iniciarEdicion(reporte)}
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                            data-testid={`edit-report-${reporte.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => eliminarReporte(reporte)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid={`delete-report-${reporte.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Info del modelo/grupo */}
                      {reporte.group_name && (
                        <p className="text-xs text-gray-500 mb-3">
                          Modelo: <span className="text-gray-400">{reporte.group_name}</span>
                        </p>
                      )}

                      {/* Desglose de facturación */}
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <div className="bg-zinc-700/50 rounded p-2 text-center">
                          <div className="flex items-center justify-center gap-1 text-purple-400 mb-1">
                            <CreditCard className="w-3 h-3" />
                            <span className="text-xs">Subs</span>
                          </div>
                          <p className="text-sm font-bold text-white">{formatFullCurrency(reporte.subs_amount)}</p>
                        </div>
                        <div className="bg-zinc-700/50 rounded p-2 text-center">
                          <div className="flex items-center justify-center gap-1 text-blue-400 mb-1">
                            <MessageSquare className="w-3 h-3" />
                            <span className="text-xs">Mensajes</span>
                          </div>
                          <p className="text-sm font-bold text-white">{formatFullCurrency(reporte.messages_amount)}</p>
                        </div>
                        <div className="bg-zinc-700/50 rounded p-2 text-center">
                          <div className="flex items-center justify-center gap-1 text-yellow-400 mb-1">
                            <Gift className="w-3 h-3" />
                            <span className="text-xs">Tips</span>
                          </div>
                          <p className="text-sm font-bold text-white">{formatFullCurrency(reporte.tips_amount)}</p>
                        </div>
                      </div>

                      {/* Total */}
                      <div className="flex items-center justify-between bg-green-500/10 rounded p-2">
                        <span className="text-sm text-gray-400">Total Facturado:</span>
                        <span className="text-lg font-bold text-green-400">
                          {formatFullCurrency(reporte.money_collected)}
                        </span>
                      </div>

                      {/* Notas */}
                      {reporte.notes && (
                        <div className="mt-2 text-xs text-gray-500">
                          <span className="font-medium">Notas:</span> {reporte.notes}
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            ))}

            {/* Total del día */}
            {reportesDelDia.length > 1 && (
              <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-400 mb-1">Total del Día</p>
                <p className="text-2xl font-bold text-green-400">
                  {formatFullCurrency(reportesDelDia.reduce((sum, r) => sum + (r.money_collected || 0), 0))}
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CalendarioVentas;
